import { useFormik } from 'formik'
import { useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Accordion } from '../../components/accordion'
import { Button } from '../../components/button'
import Icon from '../../components/icon'
import { Checkbox } from '../../components/inputs/Checkbox'
import Dropdown from '../../components/inputs/dropdown'
import { TextInput } from '../../components/inputs/textInput'
import ModalComponent from '../../components/modal'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { SelectProductModal } from '../../components/modal/app_modals/selectProductModal'
import { SKELETON_BASE_COLOR, SKELETON_HIGHLIGHT_COLOR } from '../../constants'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useAddCreatorCuratedProductMutation,
  useDeleteCreatorCuratedProductMutation,
  useGetCreatorCuratedProductsQuery,
} from '../../redux-toolkits/vendors/vendors.slice'
import {
  CuratedProductItem,
  CuratedProductTagName,
} from '../../redux-toolkits/vendors/vendors.types'
import { Product } from '../../redux-toolkits/products/products.type'
import { CuratedProductTagSchema } from '../../validation/validate'

const SLOTS = 2

const TAG_OPTIONS: {
  name: CuratedProductTagName
  value: CuratedProductTagName
}[] = [
  { name: 'NEW', value: 'NEW' },
  { name: 'SIGNATURE', value: 'SIGNATURE' },
]

export const SingleVendorCuratedProducts = ({
  creatorId,
}: {
  creatorId: string
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [selectedItem, setSelectedItem] = useState<CuratedProductItem | null>(
    null,
  )

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const { data, isFetching } = useGetCreatorCuratedProductsQuery(
    { creatorId },
    { skip: !creatorId, refetchOnMountOrArgChange: true },
  )

  const [addCuratedProduct, { isLoading: adding }] =
    useAddCreatorCuratedProductMutation()

  const [deleteCuratedProduct, { isLoading: deleting }] =
    useDeleteCreatorCuratedProductMutation()

  const curatedProducts = data?.data ?? []

  const formik = useFormik({
    initialValues: {
      tagName: '' as CuratedProductTagName | '',
      tagColor: '#000000',
      isActive: true,
    },
    validationSchema: CuratedProductTagSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!selectedProduct) return

      const resp = await addCuratedProduct({
        creatorId,
        productId: selectedProduct._id,
        tagName: values.tagName as CuratedProductTagName,
        tagColor: values.tagColor,
        isActive: values.isActive,
      })

      if ('data' in resp && resp.data?.success) {
        handleModalClose()
        setSelectedProduct(null)
        resetForm()
      }
    },
  })

  const handleProductSelected = (products: Product[]) => {
    if (!products.length) return
    setSelectedProduct(products[0])
    formik.resetForm()
    handleModalClose()
    handleModalOpen('tagConfigModal')
  }

  const handleDelete = async () => {
    if (!selectedItem) return

    const resp = await deleteCuratedProduct({
      creatorId,
      productId: selectedItem.productId._id,
    })

    if ('data' in resp && resp.data?.success) {
      handleModalClose()
      setSelectedItem(null)
    }
  }

  const emptySlots = Math.max(0, SLOTS - curatedProducts.length)

  return (
    <>
      <Accordion title="Curated Products">
        {isFetching ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: SLOTS }).map((_, i) => (
              <div
                key={i}
                className="flex flex-row bg-white border border-divider-gray rounded-md overflow-hidden min-h-[184px] p-4 gap-4"
              >
                <Skeleton
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="!rounded-md"
                  width="100%"
                  height="100%"
                  containerClassName="w-2/5 flex-shrink-0 min-h-[152px]"
                />

                <div className="flex flex-col flex-1 gap-2 pt-1">
                  <Skeleton
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    className="!rounded"
                    width="70%"
                    height={14}
                  />
                  <Skeleton
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    className="!rounded"
                    count={3}
                    height={12}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {curatedProducts.map((item) => (
              <div
                key={item._id}
                className="relative flex flex-row bg-white border border-divider-gray rounded-md overflow-hidden min-h-[184px] p-4 gap-4"
              >
                {/* Image */}
                <div className="relative w-2/5 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                  {item.productId.images?.[0] ? (
                    <img
                      src={item.productId.images[0]}
                      alt={item.productId.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100" />
                  )}

                  {item.tag?.name && (
                    <div
                      className="absolute top-2 left-2 rounded-2xl px-2 py-1 flex items-center gap-1.5"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${item.tag.color} 20%, white)`,
                        color: item.tag.color,
                      }}
                    >
                      <span
                        className="!size-[7px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.tag.color }}
                      />
                      <span className="text-[10px] font-semibold uppercase tracking-wide">
                        {item.tag.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex flex-col flex-1 gap-2">
                  <p className="text-black font-medium text-sm">
                    {item.productId.name}
                  </p>
                  <p className="text-sec-black text-sm line-clamp-3">
                    {item.productId.description}
                  </p>
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItem(item)
                    handleModalOpen('confirmDeleteModal')
                  }}
                  className="absolute top-3 right-3 p-1.5 rounded-full bg-white border border-divider-gray hover:bg-red-50 hover:border-red-200 transition-colors"
                  aria-label="Remove"
                >
                  <Icon id="bin" width={14} height={14} />
                </button>
              </div>
            ))}

            {/* Empty placeholder slots */}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <button
                key={`empty-${i}`}
                type="button"
                onClick={() => handleModalOpen('selectProductModal')}
                className="flex flex-col items-center justify-center gap-2 min-h-[184px] rounded-md border-2 border-dashed border-divider-gray bg-white hover:bg-gray-50 transition-colors text-sec-black"
              >
                <Icon id="plus-circle" width={28} height={28} />
                <span className="text-sm font-medium">Add product</span>
              </button>
            ))}
          </div>
        )}
      </Accordion>

      {/* Step 1: select product */}
      <SelectProductModal
        open={modal === 'selectProductModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={handleModalClose}
        maxSelection={1}
        selectedProducts={[]}
        onSuccessCallback={handleProductSelected}
        query={{ vendorId: creatorId }}
      />

      {/* Step 2: configure tag */}
      <ModalComponent
        open={modal === 'tagConfigModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedProduct(null)
          formik.resetForm()
        }}
        title="Configure product tag"
        childrenClassName="md:!max-w-[400px]"
      >
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
          {selectedProduct && (
            <p className="text-sm font-medium text-black">
              {selectedProduct.name}
            </p>
          )}

          <Dropdown
            menuButton={
              <TextInput
                title="Tag name"
                value={formik.values.tagName}
                placeholder="Select tag name"
                hasIcon={false}
                readOnly
                isDropdown
                errors={
                  formik.touched.tagName && formik.errors.tagName
                    ? formik.errors.tagName
                    : ''
                }
              />
            }
            menuItems={TAG_OPTIONS.map((opt) => ({
              name: opt.name,
              value: opt.value,
              onClick: () => formik.setFieldValue('tagName', opt.value),
            }))}
            className="w-full"
            menuClassName=""
            position="bottom start"
          />

          <TextInput
            title="Tag colour"
            name="tagColor"
            type="color"
            value={formik.values.tagColor}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            hasIcon={false}
            inputClass="h-8 cursor-pointer"
            errors={
              formik.touched.tagColor && formik.errors.tagColor
                ? formik.errors.tagColor
                : ''
            }
          />

          <Checkbox
            checkStatus={formik.values.isActive}
            handleChange={(val) => formik.setFieldValue('isActive', val)}
            text="Active"
          />

          <Button
            type="submit"
            primary
            label="Save"
            loading={adding}
            disabled={adding || !formik.isValid || !formik.dirty}
          />
        </form>
      </ModalComponent>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={modal === 'confirmDeleteModal' && modalOpen}
        setOpen={setModalOpen}
        title="Remove curated product"
        message={
          <>
            Are you sure you want to remove{' '}
            <span className="font-medium text-black">
              {selectedItem?.productId.name}
            </span>{' '}
            from curated products?
          </>
        }
        confirmText="Remove"
        loading={deleting}
        onCloseCallback={() => {
          handleModalClose()
          setSelectedItem(null)
        }}
        onConfirm={handleDelete}
      />
    </>
  )
}
